import React, { useState } from 'react';
import { Modal, Checkbox, Button, Upload, message } from 'antd';
import { ExclamationCircleOutlined, UploadOutlined, InboxOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

const ZeroAmountConfirmationModal = ({ visible, onConfirm, onCancel, month, year, isAnnual = false, isSemiAnnual = false, semester = null }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [dsnFile, setDsnFile] = useState(null);

  // Fonction pour formater la période au format texte
  const formatPeriod = () => {
    if (!year) return '';

    // Déclaration annuelle
    if (isAnnual) {
      return `année ${year}`;
    }

    // Déclaration semestrielle
    if (isSemiAnnual && semester) {
      return `semestre ${semester} ${year}`;
    }

    // Déclaration mensuelle (par défaut)
    if (!month) return '';

    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const monthIndex = parseInt(month, 10) - 1;
    const monthName = monthNames[monthIndex] || '';

    return `${monthName} ${year}`;
  };

  // Fonction pour déterminer le type de déclaration
  const getDeclarationType = () => {
    if (isAnnual) return 'annuelle';
    if (isSemiAnnual) return 'semestrielle';
    return 'mensuelle';
  };

  const handleConfirm = () => {
    if (confirmed && dsnFile) {
      onConfirm(dsnFile);
      // Reset for next use
      setConfirmed(false);
      setDsnFile(null);
    }
  };

  const handleCancel = () => {
    setConfirmed(false);
    setDsnFile(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          Déclaration à montant nul
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Annuler
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!confirmed || !dsnFile}
        >
          Valider la déclaration
        </Button>,
      ]}
      width={700}
      closable={false}
      maskClosable={false}
    >
      <div style={{ padding: '20px 0' }}>
        <div style={{
          padding: '16px',
          backgroundColor: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
            Vous avez fait une demande de déclaration {getDeclarationType()} pour la période <strong>{formatPeriod()}</strong> avec des montants nuls, aussi bien masse salariale qu'effectif.
          </p>
          <p style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
            Cette demande est soumise à validation de la part de l'APAS-BTP. Vous devez fournir les pièces justificatives ci-dessous afin que nous puissions traiter votre demande.
          </p>
          <p style={{ marginBottom: '0', fontSize: '14px', lineHeight: '1.6' }}>
            <strong>Documents actuellement autorisés :</strong>
          </p>
          <ul style={{ margin: '8px 0 0 20px', fontSize: '14px', lineHeight: '1.6' }}>
            <li>DSN concernant la période concernée</li>
          </ul>
        </div>

        <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.6' }}>
          <strong>Attention :</strong> Un salarié en congés maladie est soumis à cotisation.
        </p>

        <div style={{
          padding: '16px',
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ fontSize: '14px' }}
          >
            Je déclare sur l'honneur n'employer aucun salarié sur la période considérée. Une attestation de déclaration sans salarié sera générée.
          </Checkbox>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '500' }}>
            Fichier DSN requis <span style={{ color: '#ff4d4f' }}>*</span>
          </p>
          <Dragger
            name="dsnFile"
            multiple={false}
            accept=".pdf,.xml,.txt"
            maxCount={1}
            beforeUpload={(file) => {
              // Valider le type de fichier
              const isValidType = ['application/pdf', 'application/xml', 'text/xml', 'text/plain'].includes(file.type);
              if (!isValidType) {
                message.error('Seuls les fichiers PDF, XML et TXT sont acceptés !');
                return Upload.LIST_IGNORE;
              }

              // Valider la taille (10 MB max)
              const isLt10M = file.size / 1024 / 1024 < 10;
              if (!isLt10M) {
                message.error('Le fichier ne doit pas dépasser 10 MB !');
                return Upload.LIST_IGNORE;
              }

              // Limiter à un seul fichier
              setDsnFile(file);
              return false; // Empêche l'upload automatique
            }}
            onRemove={() => {
              setDsnFile(null);
            }}
            fileList={dsnFile ? [dsnFile] : []}
            itemRender={(originNode, file) => {
              // Empêche l'affichage de plusieurs fichiers
              return originNode;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Cliquez ou glissez votre fichier DSN ici</p>
            <p className="ant-upload-hint">
              Formats acceptés : PDF, XML, TXT (max 10 MB)
            </p>
          </Dragger>
        </div>

        <p style={{
          fontSize: '12px',
          color: '#666',
          fontStyle: 'italic',
          margin: 0
        }}>
          Cette déclaration générera une attestation au lieu d'une facture. Votre demande sera examinée avant validation.
        </p>
      </div>
    </Modal>
  );
};

export default ZeroAmountConfirmationModal;